import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { XCircle, Settings, Trash2, Menu } from 'lucide-react';  
import AdjustSettings from './AdjustSettings';
import StaticChoices from './StaticChoices';
import DeleteProject from './DeleteProject';
import { ProjectContext } from '../context/ProjectContext';

const Navbar3 = ({ backPath, projectId }) => {
  const navigate = useNavigate();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { state } = useContext(ProjectContext);
  
  // Add responsive state
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Track window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth >= 768) setShowMobileMenu(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowWidth < 768;

  const handleSettingsClick = () => {
    setIsSettingsOpen(true);
  };

  const handleCloseSettings = () => {
    setIsSettingsOpen(false);
  };

  const handleDeleteProject = () => {
    navigate('/projects');
  };

  return (
    <>
      <nav className="flex flex-col bg-white border-b">
        {/* Main row */}
        <div className="flex items-center justify-between p-4 md:p-6 w-full">
          <div className="text-xl md:text-2xl font-bold text-black truncate max-w-[200px] md:max-w-none">
            {state.projectTitle}
          </div>
          
          <div className="flex items-center space-x-2 md:space-x-4">
            {/* Desktop controls */}
            {!isMobile && (
              <>
                <StaticChoices projectId={projectId} />
                <button
                  onClick={handleSettingsClick}
                  className="text-gray-600 hover:text-black transition-colors duration-200"
                  title="Settings"
                >
                  <Settings size={24} />
                </button>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="text-gray-600 hover:text-red-600 transition-colors duration-200"
                  title="Delete project"
                >
                  <Trash2 size={24} />
                </button>
                <button
                  onClick={() => navigate(backPath)}
                  className="text-gray-600 hover:text-black transition-colors duration-200"
                  title="Close"
                >
                  <XCircle size={24} />
                </button>
              </>
            )}
            
            {/* Mobile menu toggle */}
            {isMobile && (
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="text-gray-600 hover:text-black transition-colors duration-200 ml-2"
                aria-label={showMobileMenu ? "Close menu" : "Open menu"}
              >
                <Menu size={24} />
              </button>
            )}
          </div>
        </div>
        
        {/* Mobile dropdown */}
        {isMobile && showMobileMenu && (
          <div className="flex flex-col p-4 border-t bg-gray-50 animate-fade-in-down">
            <div className="mb-4">
              <StaticChoices projectId={projectId} />
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={handleSettingsClick}
                className="flex items-center justify-between p-3 bg-gray-100 hover:bg-gray-200 rounded"
              >
                <span>Settings</span>
                <Settings size={20} />
              </button>
              
              <button
                onClick={() => setShowDeleteModal(true)}
                className="flex items-center justify-between p-3 bg-gray-100 hover:bg-red-100 text-red-600 hover:bg-red-200 rounded"
              >
                <span>Delete</span>
                <Trash2 size={20} />
              </button>
            </div>
            
            <button
              onClick={() => navigate(backPath)}
              className="flex items-center justify-between p-3 mt-2 bg-gray-100 hover:bg-gray-200 rounded"
            >
              <span>Close</span>
              <XCircle size={20} />
            </button>
          </div>
        )}
        
        {/* Fade-in animation */}
        <style>{`
          @keyframes fadeInDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-fade-in-down {
            animation: fadeInDown 0.2s ease-out;
          }
        `}</style>
      </nav>
      
      {isSettingsOpen && (
        <AdjustSettings projectId={projectId} onClose={handleCloseSettings} />
      )}
      
      {showDeleteModal && (
        <DeleteProject
          projectId={projectId}
          onClose={() => setShowDeleteModal(false)}
          onDelete={handleDeleteProject}
        />
      )}
    </>
  );
};

export default Navbar3;